import { ImagePlus } from "lucide-react";
import { MediaGallery } from "./MediaGallery";

/**
 * Layout variants:
 *  - "brazil": large image left (cols 1-7), text right (cols 8-12)
 *  - "korea":  text left (cols 1-5), large image right (cols 6-12)
 *  - "longboard": stacked, full-width hero image with floating spec card
 */
export const ProjectSection = ({
  id,
  index,
  eyebrow,
  title,
  subtitle,
  timeframe,
  location,
  role,
  description,
  highlights = [],
  learned = null,
  specs = [],
  primaryImage,
  galleryCount = 4,
  gallery = null,
  variant = "brazil",
  accent = "#FF3B30",
}) => {
  const isReverse = variant === "korea";
  const isLong = variant === "longboard";

  return (
    <section
      id={id}
      data-testid={`section-${id}`}
      className="relative border-t border-white/10"
    >
      {/* Section header */}
      <div className="px-6 md:px-12 lg:px-16 pt-24 md:pt-32 pb-12">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-2">
            <div className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-white/40">
              Project / {index}
            </div>
          </div>
          <div className="col-span-12 md:col-span-7">
            <div
              className="font-mono-accent text-[11px] tracking-[0.3em] uppercase"
              style={{ color: accent }}
            >
              {eyebrow}
            </div>
            <h2
              data-testid={`${id}-title`}
              className="font-display font-black uppercase mt-3 leading-[0.9] text-5xl md:text-6xl lg:text-7xl"
            >
              {title}
            </h2>
            {subtitle && (
              <div className="mt-2 font-display font-bold text-xl md:text-2xl text-white/60">
                {subtitle}
              </div>
            )}
          </div>
          <div className="col-span-12 md:col-span-3 md:text-right">
            <div className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-white/40">
              Timeframe
            </div>
            <div className="font-display font-bold text-xl mt-1">{timeframe}</div>
            {location && (
              <div className="font-mono-accent text-[11px] tracking-[0.2em] uppercase text-white/50 mt-3">
                {location}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      {!isLong ? (
        <div className="px-6 md:px-12 lg:px-16 pb-24 md:pb-32">
          <div
            className={`grid grid-cols-12 gap-px bg-white/10 border border-white/10`}
          >
            {/* Image block */}
            <div
              className={`relative col-span-12 ${
                isReverse ? "md:col-span-7 md:order-2" : "md:col-span-7"
              } bg-[#0a0a0a] overflow-hidden aspect-[4/3] md:aspect-auto md:min-h-[560px]`}
            >
              <img
                src={primaryImage}
                alt={title}
                data-testid={`${id}-hero-image`}
                className="absolute inset-0 w-full h-full object-cover img-hover"
              />
              <div className="absolute top-4 left-4 font-mono-accent text-[10px] tracking-[0.3em] uppercase text-white/80 bg-black/60 px-2 py-1 backdrop-blur-sm border border-white/10">
                Image 01 / Cover
              </div>
              <div
                className="absolute bottom-4 right-4 font-mono-accent text-[10px] tracking-[0.3em] uppercase"
                style={{ color: accent }}
              >
                ↳ Awaiting media upload
              </div>
            </div>

            {/* Text block */}
            <div
              className={`col-span-12 ${
                isReverse ? "md:col-span-5 md:order-1" : "md:col-span-5"
              } bg-[#0a0a0a] p-8 md:p-10 flex flex-col`}
            >
              <div className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-white/40">
                Role
              </div>
              <div className="font-display font-bold text-2xl mt-1">{role}</div>

              <div className="mt-8 font-mono-accent text-[10px] tracking-[0.3em] uppercase text-white/40">
                Story
              </div>
              <p className="mt-3 font-body text-base text-white/75 leading-relaxed">
                {description}
              </p>

              {highlights.length > 0 && (
                <>
                  <div className="mt-8 font-mono-accent text-[10px] tracking-[0.3em] uppercase text-white/40">
                    Highlights
                  </div>
                  <ul className="mt-3 space-y-2.5">
                    {highlights.map((h, i) => (
                      <li
                        key={i}
                        className="flex gap-3 font-body text-sm text-white/80"
                      >
                        <span
                          className="font-mono-accent text-[10px] mt-1"
                          style={{ color: accent }}
                        >
                          ◢
                        </span>
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {specs.length > 0 && (
                <div className="mt-auto pt-8 grid grid-cols-2 gap-px bg-white/10 border-t border-white/10 -mx-10 -mb-10 md:mx-0 md:mb-0">
                  {specs.map((s, i) => (
                    <div key={i} className="bg-[#0a0a0a] p-5">
                      <div className="font-mono-accent text-[9px] tracking-[0.3em] uppercase text-white/40">
                        {s.label}
                      </div>
                      <div className="font-display font-bold text-lg mt-1">
                        {s.value}
                      </div>
                      {s.sub && (
                        <div
                          className="font-body text-xs leading-snug mt-2"
                          style={{ color: accent }}
                        >
                          {s.sub}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Optional "What we learned" block */}
          {learned && (
            <div className="mt-px grid grid-cols-12 gap-px bg-white/10 border-l border-r border-b border-white/10">
              <div className="col-span-12 md:col-span-3 bg-[#0a0a0a] p-6">
                <div className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-white/40">
                  {learned.label || "What We Learned"}
                </div>
                <div className="mt-2 font-display font-bold text-lg" style={{ color: accent }}>
                  ↳ Post-mortem
                </div>
              </div>
              <div className="col-span-12 md:col-span-9 bg-[#0a0a0a] p-6 md:p-8">
                <p className="font-body text-base text-white/80 leading-relaxed">
                  {learned.text}
                </p>
              </div>
            </div>
          )}

          {/* Gallery — real media if provided, else placeholders */}
          {gallery && gallery.length > 0 ? (
            <MediaGallery id={id} items={gallery} accent={accent} />
          ) : (
            <GalleryStrip id={id} count={galleryCount} accent={accent} />
          )}
        </div>
      ) : (
        // Longboard variant: full-width image + floating spec card
        <div className="pb-24 md:pb-32">
          <div className="relative w-full aspect-[4/3] md:aspect-[16/9] bg-[#0a0a0a] overflow-hidden border-t border-b border-white/10">
            <img
              src={primaryImage}
              alt={title}
              data-testid={`${id}-hero-image`}
              className="absolute inset-0 w-full h-full object-contain img-hover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/95 via-[#050505]/30 to-transparent pointer-events-none" />
            <div className="absolute top-0 bottom-0 left-0 w-full sm:w-80 md:w-96 max-w-[90%] flex items-center">
              <div className="border-r border-y border-white/15 bg-[#050505]/85 backdrop-blur-md p-6 md:p-8 h-full w-full flex flex-col justify-center">
                <div className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-white/40">
                  Role
                </div>
                <div className="font-display font-bold text-xl mt-1">{role}</div>
                <p className="mt-5 font-body text-sm md:text-base text-white/75 leading-relaxed">
                  {description}
                </p>
              </div>
            </div>
          </div>

          <div className="px-6 md:px-12 lg:px-16 mt-12">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
              {specs.map((s, i) => (
                <div key={i} className="bg-[#0a0a0a] p-6">
                  <div className="font-mono-accent text-[9px] tracking-[0.3em] uppercase text-white/40">
                    {s.label}
                  </div>
                  <div className="font-display font-bold text-xl mt-2">
                    {s.value}
                  </div>
                </div>
              ))}
            </div>

            {highlights.length > 0 && (
              <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
                {highlights.map((h, i) => (
                  <div
                    key={i}
                    className="border border-white/10 p-6 flex gap-4 bg-[#0a0a0a]"
                  >
                    <span
                      className="font-mono-accent text-[10px] mt-1"
                      style={{ color: accent }}
                    >
                      ◢ 0{i + 1}
                    </span>
                    <p className="font-body text-sm text-white/80 leading-relaxed">
                      {h}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {gallery && gallery.length > 0 ? (
              <MediaGallery id={id} items={gallery} accent={accent} />
            ) : (
              <GalleryStrip id={id} count={galleryCount} accent={accent} />
            )}
          </div>
        </div>
      )}
    </section>
  );
};

const GalleryStrip = ({ id, count, accent }) => (
  <div className="mt-10">
    <div className="flex items-center justify-between mb-4">
      <div className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-white/40">
        Gallery / Drop-Zone
      </div>
      <div
        className="font-mono-accent text-[10px] tracking-[0.3em] uppercase"
        style={{ color: accent }}
      >
        Awaiting upload
      </div>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          data-testid={`${id}-gallery-slot-${i + 1}`}
          className="aspect-square bg-[#0a0a0a] flex flex-col items-center justify-center text-center p-4 group hover:bg-[#111] transition-colors"
        >
          <ImagePlus
            size={22}
            className="text-white/30 group-hover:text-white/70 transition-colors"
          />
          <div className="mt-3 font-mono-accent text-[9px] tracking-[0.3em] uppercase text-white/30">
            Slot 0{i + 1}
          </div>
          <div className="font-mono-accent text-[9px] tracking-[0.2em] text-white/20 mt-1">
            photo / video
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const SectionDivider = ({ text }) => (
  <div className="relative overflow-hidden border-t border-b border-white/10 bg-[#050505] py-6">
    <div className="marquee flex gap-12 font-mono-accent text-[11px] tracking-[0.3em] uppercase text-white/30">
      {Array.from({ length: 4 }).map((_, i) => (
        <span key={i} className="shrink-0 flex items-center gap-6">
          {text} <span style={{ color: "#FF3B30" }}>◢</span>
        </span>
      ))}
    </div>
  </div>
);

// Re-export so consumer can import both
export const ProjectBlock = ProjectSection;
